import { Product } from "~/utils/Product";
import { Article, type ArticleAssignmentSummary } from "~/utils/Article";

export type CaseExtraContent = {
  id: number
  product: Product | null
  quantity: number
  note: string
}

function parseCaseContent(entry: any, index: number): CaseExtraContent | null {
  let product: Product | null = null
  if (entry?.product?.data) {
    product = new Product()
    product.parseJson(entry.product.data)
  }

  if (!product) {
    return null
  }

  return {
    id: entry.id ?? index,
    product,
    quantity: entry?.quantity != null ? Number(entry.quantity) : 0,
    note: entry?.note ?? "",
  }
}

export class Case {
  id = -1
  name = ""
  barcode = ""
  targetContents: CaseExtraContent[] = []
  articles: Article[] = []
  extraContents: CaseExtraContent[] = []
  openInternalContents: CaseExtraContent[] = []
  activeEvent: ArticleAssignmentSummary | null = null
  activeInternalUse = false
  notes = ""
  isActive = true
  readinessStatus = "unknown"
  lastCheckedAt = ""

  fromJSON(obj: Partial<Case>): Case {
    return Object.assign(new Case(), obj)
  }

  parseJson(ret: { id: number; attributes: { [key: string]: any } }) {
    this.id = ret.id
    this.name = ret.attributes.name ?? ""
    this.barcode = ret.attributes.barcode ?? ""
    this.notes = ret.attributes.notes ?? ""
    this.isActive = ret.attributes.isActive ?? true
    this.readinessStatus = ret.attributes.readinessStatus ?? "unknown"
    this.lastCheckedAt = ret.attributes.lastCheckedAt ?? ""
    this.activeEvent = this.parseAssignment(ret.attributes.activeEvent?.data)
    this.activeInternalUse = ret.attributes.activeInternalUse ?? false

    this.targetContents = Array.isArray(ret.attributes.targetContents)
      ? ret.attributes.targetContents
          .map((entry: any, index: number) => parseCaseContent(entry, index))
          .filter((entry: CaseExtraContent | null): entry is CaseExtraContent => !!entry)
      : []

    this.articles = Array.isArray(ret.attributes.articles?.data)
      ? ret.attributes.articles.data.map((entry: any) => {
          const article = new Article()
          article.parseJson(entry)
          return article
        })
      : []

    this.extraContents = Array.isArray(ret.attributes.extraContents)
      ? ret.attributes.extraContents
          .map((entry: any, index: number) => parseCaseContent(entry, index))
          .filter((entry: CaseExtraContent | null): entry is CaseExtraContent => !!entry)
      : []

    this.openInternalContents = Array.isArray(ret.attributes.openInternalContents)
      ? ret.attributes.openInternalContents
          .map((entry: any, index: number) => parseCaseContent(entry, index))
          .filter((entry: CaseExtraContent | null): entry is CaseExtraContent => !!entry)
      : []
  }

  private parseAssignment(value: any): ArticleAssignmentSummary | null {
    if (!value) {
      return null
    }
    return {
      id: value.id,
      title: value.Name ?? value.attributes?.Name ?? `Eintrag ${value.id}`
    }
  }

  get contentSummary(): string {
    const grouped = new Map<string, number>()
    this.articles.forEach((article) => {
      const key = article.product?.Name ?? article.article_number
      grouped.set(key, (grouped.get(key) ?? 0) + 1)
    })

    const summary = Array.from(grouped.entries()).map(([name, count]) => `${count}x ${name}`)
    this.extraContents.forEach((entry) => {
      if (entry.product) {
        summary.push(`${entry.quantity}x ${entry.product.Name}`)
      }
    })

    return summary.length ? summary.join(", ") : "Keine Inhalte"
  }

  toJSON() {
    return {
      name: this.name,
      barcode: this.barcode,
      targetContents: this.targetContents
        .filter((entry) => entry.product?.id && entry.quantity > 0)
        .map((entry) => ({
          product: entry.product!.id,
          quantity: entry.quantity,
          note: entry.note ?? "",
        })),
      articles: this.articles.map((article) => article.id).filter((id) => id > 0),
      extraContents: this.extraContents
        .filter((entry) => entry.product?.id && entry.quantity > 0)
        .map((entry) => ({
          product: entry.product!.id,
          quantity: entry.quantity,
          note: entry.note ?? "",
        })),
      openInternalContents: this.openInternalContents
        .filter((entry) => entry.product?.id && entry.quantity > 0)
        .map((entry) => ({
          product: entry.product!.id,
          quantity: entry.quantity,
          note: entry.note ?? "",
        })),
      activeEvent: this.activeEvent?.id ?? null,
      activeInternalUse: this.activeInternalUse,
      notes: this.notes,
      isActive: this.isActive,
      readinessStatus: this.readinessStatus,
      lastCheckedAt: this.lastCheckedAt || null,
    }
  }
}
